import axios, { Canceler } from 'axios'
import { axiosInstance } from './http-common.service'
import { HttpService } from './http.service'
import { EnhancedWithAuthHttpService } from './http-auth.service'

import {
  IAcceptAccountUserInviteRespDto,
  IUpdateAccountAnalyticsDto,
  IAccountByIdRespDto,
  IAccountCreateCheckDto,
  IAccountCreateCheckRespDto,
  IAccountCreateDto,
  IAccountExposePrivateKeyRespDto,
  IAccountManualCreateDto,
  IAccountManualCreateRespDto,
  IAccountRemoveUsersDto,
  IAccountsAllRawRespDto,
  IAccountsAllRespDto,
  IAccountScannerInviteDto,
  IAccountScannerInviteRespDto,
  IAccountScannerRespDto,
  IAccountsRespDto,
  IAccountSuspensionDto,
  IAccountUpdateDto,
  IAccountUpdateOwnerManualDto,
  IAccountUpdateUserRolesDto,
  IAccountUpdateUserRolesRespDto,
  IAccountUserInviteDto,
  IAccountUserInviteRespDto,
  IAccountUserResponseDto,
  ICancelRemovalDto,
  IMessageResponseDto,
  IUpdateAccountWelcomeShowDto,
  IUpdateAccountSubcriptionPricesDto,
  IDefaultSubscriptionPricesRespDto,
  IUpdateAccountJourneySettingsDto,
  IAccountEnterpriseDto,
  IUpdateAccountQrLinkDto,
  IAccountQrLinkRespDto,
  IUpdateAccountPublicStatusDto,
} from 'src/types'

class AccountsService extends EnhancedWithAuthHttpService {
  CancelTokenAccountsAll
  cancelAllAccounts?: Canceler
  CancelTokenAccountsAllRaw
  cancelAllAccountsRaw?: Canceler
  CancelTokenAccounts
  cancelAccounts?: Canceler

  constructor() {
    super(new HttpService(axiosInstance))
    this.CancelTokenAccountsAll = axios.CancelToken
    this.CancelTokenAccounts = axios.CancelToken
    this.CancelTokenAccountsAllRaw = axios.CancelToken
  }

  async remintAccountToken(id: string) {
    return await this.post<IMessageResponseDto, null>(`accounts/${id}/token`, null)
  }

  async updateAccountTokenURI(id: string) {
    return await this.put<IMessageResponseDto, null>(`accounts/${id}/token-uri`, null)
  }

  async updateAccountWelcome(dto: IUpdateAccountWelcomeShowDto) {
    return await this.put<IMessageResponseDto, IUpdateAccountWelcomeShowDto>(
      `accounts/change/welcome`,
      dto,
    )
  }

  async updateAccountAnalytics(dto: IUpdateAccountAnalyticsDto, id: string) {
    return await this.put<IMessageResponseDto, IUpdateAccountAnalyticsDto>(
      `accounts/${id}/analytics`,
      dto,
    )
  }

  async updateAccount(dto: IAccountUpdateDto, id: string) {
    return await this.put<IMessageResponseDto, IAccountUpdateDto>(`accounts/${id}`, dto)
  }

  async getAccount(id: string, locale: string) {
    return await this.get<IAccountByIdRespDto>(`accounts/${id}/locale/${locale}`)
  }

  async getAccountSubscriptionPlan(id: string) {
    return await this.get<{ subscription_plan: string }>(`accounts/${id}/subscription`)
  }

  async createAccount(dto: IAccountCreateDto) {
    return await this.post<IMessageResponseDto, IAccountCreateDto>('accounts', dto)
  }

  async createAccountCheck(dto: IAccountCreateCheckDto) {
    return this.put<IAccountCreateCheckRespDto, IAccountCreateCheckDto>('accounts/check', dto)
  }

  async createAccountCheckAccept(accountId: string) {
    return this.put<IMessageResponseDto, { accountId: string }>('accounts/check/accept', {
      accountId,
    })
  }

  getAccounts(params: any) {
    if (this.cancelAccounts) {
      this.cancelAccounts()
    }

    return this.get<IAccountsRespDto>('accounts', {
      params,
      cancelToken: new this.CancelTokenAccounts((c) => {
        this.cancelAccounts = c
      }),
    })
  }

  getAllAccounts(params?: {}) {
    if (this.cancelAllAccounts) {
      this.cancelAllAccounts()
    }

    return this.get<IAccountsAllRespDto>('accounts/all', {
      params,
      cancelToken: new this.CancelTokenAccountsAll((c) => {
        this.cancelAllAccounts = c
      }),
    })
  }

  getAllAccountsRaw(params?: {}) {
    if (this.cancelAllAccountsRaw) {
      this.cancelAllAccountsRaw()
    }

    return this.get<IAccountsAllRawRespDto>('accounts/all/raw', {
      params,
      cancelToken: new this.CancelTokenAccountsAllRaw((c) => {
        this.cancelAllAccountsRaw = c
      }),
    })
  }

  getAccountUsers(params: any, id: string) {
    return this.get<IAccountUserResponseDto[]>(`accounts/${id}/users`, { params })
  }

  sendAccountUserInvite(id: string, dto: IAccountUserInviteDto) {
    return this.post<IAccountUserInviteRespDto, IAccountUserInviteDto>(`accounts/${id}/users`, dto)
  }

  sendAccountScannerInvite(id: string, dto: IAccountScannerInviteDto) {
    return this.post<IAccountScannerInviteRespDto, IAccountScannerInviteDto>(
      `accounts/${id}/users/scanner`,
      dto,
    )
  }

  getAccountScanners(id: string, params: {}) {
    return this.get<IAccountScannerRespDto[]>(`accounts/${id}/scanners`, { params })
  }

  resendAccountUserInvite(receiverUserId: string, accountId: string) {
    return this.put<IMessageResponseDto, {}>(`accounts/${accountId}/users/resend`, {
      receiverUserId,
    })
  }

  acceptAccountUserInvite(token: string) {
    return this.put<IAcceptAccountUserInviteRespDto, {}>(
      `accounts/users/accept-invite/${token}`,
      {},
    )
  }

  acceptAccountOwnership(token: string) {
    return this.put<IAcceptAccountUserInviteRespDto, {}>(
      `accounts/users/accept-ownership/${token}`,
      {},
    )
  }

  updateIcon(id: string, images: (string | null)[]) {
    return this.put<IMessageResponseDto, { images: (string | null)[] }>(`accounts/${id}/icon`, {
      images,
    })
  }

  removeAccount(id: string) {
    return this.delete<IMessageResponseDto>(`accounts/${id}`)
  }

  verifyAccountOwnerTransfer(token: string, code: string, accountId: string) {
    return this.put<IMessageResponseDto, { code: string }>(
      `accounts/${accountId}/users/roles/verify/${token}`,
      { code },
    )
  }

  removeAccountUsers(id: string, dto: IAccountRemoveUsersDto) {
    return this.delete<IMessageResponseDto>(`accounts/${id}/users`, { data: dto })
  }

  cancelRemove(params: ICancelRemovalDto) {
    return this.get<IMessageResponseDto>('accounts/cancel-deletion', { params: params })
  }

  removeAccountIcon(id: string) {
    return this.delete<IMessageResponseDto>(`accounts/${id}/icon-remove`)
  }

  updateAccountUsersRole(id: string, dto: IAccountUpdateUserRolesDto) {
    return this.put<IAccountUpdateUserRolesRespDto, IAccountUpdateUserRolesDto>(
      `accounts/${id}/users/roles`,
      dto,
    )
  }

  updateAccountOwnerManual(id: string, dto: IAccountUpdateOwnerManualDto) {
    return this.put<IAccountUpdateUserRolesRespDto, IAccountUpdateOwnerManualDto>(
      `accounts/${id}/users/roles/owner/manual`,
      dto,
    )
  }

  updateAccountSuspension({ id, suspended }: IAccountSuspensionDto) {
    return this.put<IMessageResponseDto, { suspended: boolean }>(`accounts/${id}/suspension`, {
      suspended,
    })
  }

  updateAccountEnterprise({ id, assign }: IAccountEnterpriseDto) {
    return this.put<IMessageResponseDto, { assigned: boolean }>(`accounts/${id}/enterprise`, {
      assigned: assign,
    })
  }

  updateAccount2FARequierd(accountId: string, required: boolean) {
    return this.put<IMessageResponseDto, { required: boolean }>(`accounts/${accountId}/2fa`, {
      required,
    })
  }

  updateAccountPublicStatus(id: string, dto: IUpdateAccountPublicStatusDto) {
    return this.put<IMessageResponseDto, IUpdateAccountPublicStatusDto>(
      `accounts/${id}/public`,
      dto,
    )
  }

  useUpdateAccountJourneySettings(accountId: string, values: IUpdateAccountJourneySettingsDto) {
    return this.put<IMessageResponseDto, any>(`accounts/${accountId}/journey-settings`, {
      ...values,
    })
  }

  getAccountPrices(accountId: string) {
    return this.get<IDefaultSubscriptionPricesRespDto>(`accounts/${accountId}/prices`)
  }

  updateAccountPrices(accountId: string, dto: IUpdateAccountSubcriptionPricesDto) {
    return this.put<IMessageResponseDto, IUpdateAccountSubcriptionPricesDto>(
      `accounts/${accountId}/prices`,
      dto,
    )
  }

  createAccountManual(dto: IAccountManualCreateDto) {
    return this.post<IAccountManualCreateRespDto, IAccountManualCreateDto>('accounts/manual', dto)
  }

  requestPrivateKey(accountId: string) {
    return this.put<IMessageResponseDto, null>(`accounts/${accountId}/wallets/pk`, null)
  }

  retrievePrivateKey(accountId: string, code: string) {
    return this.put<IAccountExposePrivateKeyRespDto, { code: string }>(
      `accounts/${accountId}/wallets/pk/verify`,
      { code },
    )
  }

  getAccountSubscription(accountId?: string) {
    return this.get<boolean>(`accounts/current-subscription/${accountId}`)
  }

  setAccountWlp(data: object) {
    return this.put<IMessageResponseDto, any>(`accounts/assign-white-label`, {
      data,
    })
  }

  updateScannerSteps(scannerId: string, accountId: string, steps: string[]) {
    return this.post<IMessageResponseDto, any>('accounts/connect-step-to-scanner', {
      accountId,
      scannerId,
      steps,
    })
  }

  getOverrideUrl(id?: string, lngCode?: string) {
    return this.get<IAccountQrLinkRespDto>(`accounts/${id}/urls/${lngCode}`)
  }

  updateQrLink(dto: IUpdateAccountQrLinkDto) {
    return this.put<IMessageResponseDto, Omit<IUpdateAccountQrLinkDto, 'id'>>(
      `accounts/${dto.id}/urls`,
      { overrideUrl: dto.overrideUrl, code: dto.code },
    )
  }

  updateAccountNfts() {
    return this.put<IMessageResponseDto, any>(`crone-jobs/update-account-nfts`, {})
  }
}

export const accountsService = new AccountsService()

export default AccountsService
